'use client';

import { Button } from '@components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@components/ui/dropdown-menu';
import { Plus } from 'lucide-react';

import { useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState, useRef, type ReactElement } from 'react';
import { useReducedMotion } from 'motion/react';
import dynamic from 'next/dynamic';
import { createLogbookDraft } from '@app/server-actions/logbook';
import { toast } from 'sonner';
import { FloatingCreateMenu } from './mobile/navigation/floating-create-menu';
const CreateDirectMessageDialog = dynamic(
  () => import('@components/CreateDirectMessageDialog')
);
import { cn } from '@utils/cn';
import type { OrganizationUserRole } from '@ent/permissions-shared';
import {
  CAPABILITIES,
  hasCapability,
  type Capability
} from '@utils/capabilities';
import { useCurrentOrgUser } from '../zero/use-current-user';
import {
  AddCreateMenuItemId,
  addCreateMenuLeaves,
  filterAddCreateMenu,
  getAddCreateMenu,
  getContextualAddCreateItemIds
} from '../utils/user-utils';
import { useOptionalEntitlements } from './billing/EntitlementsProvider';
import { FEATURES, type FeatureId } from '@billing/plans';
import { getHref, getNewChannelHref, ROUTE } from '../utils/route-utils';
import { usePageChrome } from '@components/layout/page-chrome-context';

const CREATE_CAPABILITIES: Partial<Record<AddCreateMenuItemId, Capability>> = {
  [AddCreateMenuItemId.CreateChannel]: CAPABILITIES.ManageChannels,
  [AddCreateMenuItemId.CreateDirectMessage]: CAPABILITIES.SendMessages,
  [AddCreateMenuItemId.Add86Item]: CAPABILITIES.ManageUnavailableItems,
  [AddCreateMenuItemId.CreateBeverageMenu]: CAPABILITIES.ManageMenus,
  [AddCreateMenuItemId.CreateFoodMenu]: CAPABILITIES.ManageMenus,
  [AddCreateMenuItemId.ImportBeverageMenu]: CAPABILITIES.ManageMenus,
  [AddCreateMenuItemId.ImportFoodMenu]: CAPABILITIES.ManageMenus,
  [AddCreateMenuItemId.CreateRecipe]: CAPABILITIES.ManageRecipes,
  [AddCreateMenuItemId.CreateArticle]: CAPABILITIES.ManageReference,
  [AddCreateMenuItemId.AddToManagerLog]: CAPABILITIES.CreateLogbookEntry,
  [AddCreateMenuItemId.InviteEmployee]: CAPABILITIES.ManageTeam
};

/** Plan feature each create action needs; locked items are hidden. */
const CREATE_FEATURES: Partial<Record<AddCreateMenuItemId, FeatureId>> = {
  [AddCreateMenuItemId.CreateChannel]: FEATURES.Messages,
  [AddCreateMenuItemId.CreateDirectMessage]: FEATURES.Messages,
  [AddCreateMenuItemId.Add86Item]: FEATURES.EightySix,
  [AddCreateMenuItemId.CreateBeverageMenu]: FEATURES.Menus,
  [AddCreateMenuItemId.CreateFoodMenu]: FEATURES.Menus,
  [AddCreateMenuItemId.ImportBeverageMenu]: FEATURES.AiMenuImport,
  [AddCreateMenuItemId.ImportFoodMenu]: FEATURES.AiMenuImport,
  [AddCreateMenuItemId.CreateRecipe]: FEATURES.HouseRecipes,
  [AddCreateMenuItemId.AddToManagerLog]: FEATURES.Logbook,
  [AddCreateMenuItemId.InviteEmployee]: FEATURES.Team
};

/** FloatingCreateMenu's 220ms morph-out plus a frame. Opening the DM sheet
 * while the menu's modal is still exiting stacks two scroll locks and focus
 * traps (iOS then rubber-bands the sheet and can lose its footer). */
const FAB_MENU_EXIT_MS = 260;

const MENU_ROW =
  'min-h-[var(--component-row-height)] rounded-xl px-4 py-3 text-body font-medium whitespace-normal';

export type Props = {
  organization_id: string | undefined;
  /**
   * `sidebar` renders the full-width button (dropdown). `fab` renders the floating create button above the tab bar.
   * `island` is the Plus squircle next to the mobile tab island.
   * `chrome` is the acid-lime circle in the mobile header.
   */
  variant?: 'sidebar' | 'fab' | 'island' | 'chrome';
};

const AppAddCreateButton = ({
  organization_id,
  variant = 'sidebar'
}: Props) => {
  const t = useTranslations();
  const creatingLog = useRef(false);
  const [directMessageOpen, setDirectMessageOpen] = useState(false);
  const reducedMotion = useReducedMotion();
  const deferredOpen = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (deferredOpen.current !== null)
        window.clearTimeout(deferredOpen.current);
    },
    []
  );
  // The fab's multi-action menu is a modal Radix dialog with an exit
  // animation; wait for it to unmount before mounting the DM sheet. Set
  // below once the action count is known; read at click time.
  let exitsMenuFirst = false;
  const openDirectMessage = () => {
    if (deferredOpen.current !== null)
      window.clearTimeout(deferredOpen.current);
    if (!exitsMenuFirst) {
      setDirectMessageOpen(true);
      return;
    }
    deferredOpen.current = window.setTimeout(
      () => {
        deferredOpen.current = null;
        setDirectMessageOpen(true);
      },
      reducedMotion ? 0 : FAB_MENU_EXIT_MS
    );
  };
  const router = useRouter();
  const pathname = usePathname();
  const { chrome } = usePageChrome();
  const orgUser = useCurrentOrgUser(organization_id ?? '');
  const role = (orgUser?.role as OrganizationUserRole | undefined) ?? null;
  const scope = organization_id
    ? getContextualAddCreateItemIds(pathname, organization_id)
    : 'none';
  const entitlements = useOptionalEntitlements();
  const menuItems = filterAddCreateMenu(
    getAddCreateMenu(t, {
      onCreateChannel: () => router.push(getNewChannelHref(organization_id)),
      onCreateDirectMessage: openDirectMessage,
      onAdd86Item: () =>
        router.push(getHref(organization_id, [ROUTE.EIGHTY_SIX, 'add'])),
      onCreateBeverageMenu: () =>
        router.push(
          getHref(organization_id, [
            ROUTE.BEVERAGES,
            ROUTE.COCKTAIL_MENUS,
            'create-menu'
          ])
        ),
      onCreateFoodMenu: () =>
        router.push(
          getHref(organization_id, [
            ROUTE.FOOD,
            ROUTE.FOOD_MENUS,
            'create-menu'
          ])
        ),
      onImportBeverageMenu: () =>
        router.push(
          getHref(organization_id, [
            ROUTE.BEVERAGES,
            ROUTE.COCKTAIL_MENUS,
            'import-menu'
          ])
        ),
      onImportFoodMenu: () =>
        router.push(
          getHref(organization_id, [
            ROUTE.FOOD,
            ROUTE.FOOD_MENUS,
            'import-menu'
          ])
        ),
      onCreateRecipe: () =>
        router.push(
          getHref(organization_id, [ROUTE.BEVERAGES, ROUTE.RECIPES, 'create'])
        ),
      onCreateArticle: () =>
        router.push(
          getHref(organization_id, [
            ROUTE.LIBRARY,
            ROUTE.LIBRARY_REFERENCE,
            'manage',
            'new'
          ])
        ),
      onAddToManagerLog: () => {
        if (!organization_id || creatingLog.current) return;
        creatingLog.current = true;
        void createLogbookDraft({
          organizationId: organization_id,
          logId: crypto.randomUUID()
        })
          .then(result => {
            if (result.ok)
              router.push(
                getHref(organization_id, [ROUTE.LOGBOOK, result.value, 'edit'])
              );
            else toast.error(t('logbook.logbook-error'));
          })
          .catch(() => toast.error(t('logbook.logbook-error')))
          .finally(() => {
            creatingLog.current = false;
          });
      },
      onInviteEmployee: () =>
        router.push(getHref(organization_id, ['add-employee']))
    }).map(item => ({
      ...item,
      children: item.children?.filter(child => {
        const requiredCapability =
          CREATE_CAPABILITIES[child.id as AddCreateMenuItemId];
        if (
          !requiredCapability ||
          !hasCapability(role ?? undefined, requiredCapability)
        ) {
          return false;
        }
        const requiredFeature =
          CREATE_FEATURES[child.id as AddCreateMenuItemId];
        return requiredFeature && entitlements
          ? entitlements.has(requiredFeature)
          : true;
      })
    })),
    scope
  );
  const leafActions = addCreateMenuLeaves(menuItems);
  const hidePlus =
    (variant === 'fab' && Boolean(chrome?.hideMobileTabs)) ||
    leafActions.length === 0;

  const selectItem = (onClick?: () => void) => {
    onClick?.();
  };

  const hasMenu = leafActions.length > 1;
  exitsMenuFirst = variant === 'fab' && hasMenu;

  // A single action skips the menu and runs directly.
  const onPlusClick = hasMenu ? undefined : () => leafActions[0]?.onClick?.();

  const plusLabel =
    leafActions.length === 1 && leafActions[0]?.label
      ? leafActions[0].label
      : t('common.add-create');

  if (hidePlus) {
    return null;
  }

  const withMenu = (
    trigger: ReactElement,
    { side, className }: { side: 'top' | 'bottom'; className?: string }
  ) =>
    hasMenu ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent
          side={side}
          align="end"
          sideOffset={8}
          collisionPadding={20}
          aria-label={t('common.add-create')}
          className={cn(
            'navigation-glass navigation-glass-menu max-h-[var(--radix-dropdown-menu-content-available-height)] w-[min(20rem,calc(100vw-40px))] overflow-y-auto rounded-xl border-0 p-2 text-popover-foreground',
            className
          )}
        >
          {menuItems.map((item, i) => {
            if (item.id === 'separator') {
              return (
                <DropdownMenuSeparator
                  key={`${item.id}-${i}`}
                  className="mx-4 bg-divider"
                />
              );
            }

            return (
              <DropdownMenuGroup key={item.id}>
                {item.children?.map((child, j) => (
                  <DropdownMenuItem
                    key={`${child.id}-${j}`}
                    className={MENU_ROW}
                    onClick={() => selectItem(child.onClick)}
                  >
                    {child.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    ) : (
      trigger
    );

  const dialog =
    directMessageOpen && organization_id ? (
      <CreateDirectMessageDialog
        organization_id={organization_id}
        open
        onOpenChange={setDirectMessageOpen}
        onCreated={id =>
          router.push(getHref(organization_id, [ROUTE.MESSAGES, id]))
        }
      />
    ) : null;
  if (variant === 'fab')
    return (
      <>
        <FloatingCreateMenu actions={leafActions} />
        {dialog}
      </>
    );

  if (variant === 'island') {
    return withMenu(
      <button
        type="button"
        aria-label={plusLabel}
        onClick={onPlusClick}
        className="chrome-glass inline-flex size-[var(--mobile-tab-island-height)] shrink-0 items-center justify-center rounded-[var(--mobile-chrome-squircle)] text-foreground transition-transform duration-fast ease-out active:scale-[0.97]"
      >
        <Plus className="size-5" />
      </button>,
      { side: 'top' }
    );
  }

  if (variant === 'chrome') {
    return (
      <>
        {withMenu(
          <Button
            type="button"
            size="icon"
            variant="primary"
            aria-label={plusLabel}
            onClick={onPlusClick}
            className="size-11 rounded-full"
          >
            <Plus className="size-5" aria-hidden />
          </Button>,
          { side: 'bottom' }
        )}
        {dialog}
      </>
    );
  }

  return withMenu(
    <Button className="flex items-center w-full my-2" onClick={onPlusClick}>
      <Plus />
      {t('common.add-create')}
    </Button>,
    { side: 'bottom', className: 'min-w-56' }
  );
};

export default AppAddCreateButton;
